import React from 'react';
import { useGPS } from '../../contexts/GPSContext';
import { safeParseDate } from '../../utils/dateUtils';
import GPSKpiTile from './GPSKpiTile';

const IconInbox = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);
const IconCheck = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const IconWrench = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6 2 2 6-6a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2z" />
  </svg>
);
const IconBalance = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 7-8.5 8.5-5-5L2 17" /><path d="M16 7h6v6" />
  </svg>
);
const IconStore = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4" />
  </svg>
);
const IconGauge = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" />
  </svg>
);

const IconSwap = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m17 2 4 4-4 4" /><path d="M3 6h18" /><path d="m7 22-4-4 4-4" /><path d="M21 18H3" />
  </svg>
);

// Per-mode visual identity (matches the focused blocks of the mockup).
const THEMES = {
  all: {
    title: 'Mouvements de la période',
    subtitle: 'Événements datés survenus pendant la période',
    section: 'from-[#f6f5ff] to-[#fbfbfe] border-[#e6e3fb] dark:from-[#1c1830] dark:to-[#161421] dark:border-gray-900',
    iconBg: '#5b3df0',
    badge: 'text-[#5b3df0] dark:text-[#a98ff5] bg-[#ece8fd] dark:bg-[#2a2440]',
    badgeText: 'FLUX',
    headerIcon: IconSwap,
  },
  received: {
    title: 'Tickets reçus',
    subtitle: 'Tickets créés pendant la période',
    section: 'from-[#f4f8ff] to-[#fbfcfe] border-[#d8e6fb] dark:from-[#0f1a2b] dark:to-[#0d1420] dark:border-gray-900',
    iconBg: '#2f6df0',
    badge: 'text-[#2f6df0] dark:text-blue-200 bg-[#e6f0ff] dark:bg-blue-900/40',
    badgeText: 'FLUX · ENTRÉES',
    headerIcon: IconInbox,
  },
  resolved: {
    title: 'Tickets fermés',
    subtitle: 'Tickets clôturés pendant la période',
    section: 'from-[#f5fbf7] to-[#fbfdfc] border-[#d6ecdf] dark:from-[#0f1f17] dark:to-[#0d1612] dark:border-gray-900',
    iconBg: '#16a866',
    badge: 'text-[#15784c] dark:text-green-200 bg-[#e2f5ea] dark:bg-green-900/40',
    badgeText: 'FLUX · SORTIES',
    headerIcon: IconCheck,
  },
};

// Solde net coloration (dynamic by sign):
//  - positive (backlog grows)   → warning orange #f5a623 (= aging "à surveiller").
//  - negative (backlog resorbs) → graph green #16a866 (= aging "récents" / status DONE).
//  - zero (stable)              → neutral slate.
// Gradients start a shade darker than the named color so white text stays legible.
const getSoldeFill = (solde) => {
  if (solde > 0) {
    return { gradient: 'linear-gradient(135deg,#d9870f,#f5a623)', valueColor: '#fff', labelColor: '#fdeccb', infoColor: '#fdeccb', trend: 'Le backlog gonfle ↑' };
  }
  if (solde < 0) {
    return { gradient: 'linear-gradient(135deg,#0f8a53,#16a866)', valueColor: '#fff', labelColor: '#c6efd9', infoColor: '#c6efd9', trend: 'Le backlog se résorbe ↓' };
  }
  return { gradient: 'linear-gradient(135deg,#2b3346,#3a4a63)', valueColor: '#fff', labelColor: '#aeb4c4', infoColor: '#aeb4c4', trend: 'Backlog stable →' };
};

const GPSFluxBlock = ({ stats, mode = 'all' }) => {
  const gps = useGPS();
  const theme = THEMES[mode] || THEMES.all;

  const recus = stats.openTickets.length;
  const resolus = stats.closedTickets.length;
  const interventions = stats.interventionsDone.length;
  const solde = recus - resolus;

  // Reçus / jour over the selected range (inclusive).
  const dayMs = 1000 * 60 * 60 * 24;
  const now = new Date();
  const effectiveEndDate = new Date(gps.endDate) > now ? now : new Date(gps.endDate);
  const nbDays = Math.max(1, Math.round((effectiveEndDate - new Date(gps.startDate)) / dayMs) + 1);
  const previousNbDays = Math.max(1, Math.round((new Date(gps.previousEndDate) - new Date(gps.previousStartDate)) / dayMs) + 1);

  const recusPerDay = parseFloat((recus / nbDays).toFixed(1));
  const previousRecusPerDay = parseFloat((stats.previous.openTickets.length / previousNbDays).toFixed(1));

  // Average resolution delay (creation → closure) over resolved tickets.
  const avgResolutionDays = (() => {
    const deltas = [];
    (stats.closedTickets || []).forEach((issue) => {
      const created = safeParseDate(issue.created);
      const closed = safeParseDate(issue.status_date);
      if (created && closed && closed >= created) deltas.push((closed - created) / dayMs);
    });
    if (!deltas.length) return null;
    return Math.round(deltas.reduce((a, b) => a + b, 0) / deltas.length);
  })();

  const gridCols = mode === 'all' ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-3';
  const soldeFill = getSoldeFill(solde);
  const soldeDisplay = `${solde > 0 ? '+' : ''}${solde.toLocaleString('fr-FR')}`;

  return (
    <section className={`bg-gradient-to-b border ${theme.section} rounded-2xl p-4 h-full flex flex-col`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-white flex-shrink-0" style={{ background: theme.iconBg }}>
          {React.cloneElement(theme.headerIcon, { className: 'w-[18px] h-[18px]', strokeWidth: 2 })}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-base font-bold text-gray-900 dark:text-white">{theme.title}</span>
            <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded ${theme.badge}`}>
              {theme.badgeText}
            </span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {theme.subtitle}
          </div>
        </div>
      </div>

      <div className={`grid ${gridCols} gap-2.5 flex-1 auto-rows-fr`}>
        {mode === 'all' && (
          <>
            <GPSKpiTile label="Reçus" value={recus} previousValue={stats.previous.openTickets.length} icon={IconInbox} description="Tickets créés durant la période sélectionnée. Les Brouillons sont exclus." />
            <GPSKpiTile label="Fermés" value={resolus} previousValue={stats.previous.closedTickets.length} icon={IconCheck} description="Tickets clôturés pendant la période, quelle que soit leur date de création, quelque soit leur statut de fermeture (Résolu, Rejeté, Annulé...)." />
            <GPSKpiTile label="Interventions" value={interventions} previousValue={stats.previous.interventionsDone.length} icon={IconWrench} description="Tickets au statut « Terminé » avec une date d'intervention planifiée, comptés une fois par magasin et par jour." />
            <GPSKpiTile
              label="Solde net"
              value={soldeDisplay}
              icon={IconBalance}
              fill={soldeFill}
              description="Reçus moins fermés sur la période. Positif : le backlog gonfle ; négatif : il se résorbe."
              additionalInfo={(
                <>
                  <div>{recus} reçus − {resolus} fermés</div>
                  <div className="font-semibold mt-0.5">{soldeFill.trend}</div>
                </>
              )}
            />
          </>
        )}

        {mode === 'received' && (
          <>
            <GPSKpiTile label="Reçus" value={recus} previousValue={stats.previous.openTickets.length} icon={IconInbox} description="Tickets créés durant la période sélectionnée. Les Brouillons sont exclus." />
            <GPSKpiTile label="Reçus / jour" value={recusPerDay} previousValue={previousRecusPerDay} icon={IconGauge} description="Nombre moyen de tickets reçus par jour (reçus ÷ nombre de jours de la période)." />
            <GPSKpiTile label="Magasins concernés" value={stats.distinctsInterventionPoints.length} previousValue={stats.previous.distinctsInterventionPoints.length} icon={IconStore} description="Nombre de magasins distincts ayant généré au moins un ticket reçu sur la période." />
          </>
        )}

        {mode === 'resolved' && (
          <>
            <GPSKpiTile label="Fermés" value={resolus} previousValue={stats.previous.closedTickets.length} icon={IconCheck} description="Tickets clôturés pendant la période, quelle que soit leur date de création, quelque soit leur statut de fermeture (Résolu, Rejeté, Annulé...)." />
            <GPSKpiTile label="Délai moyen de résolution" value={avgResolutionDays === null ? '—' : `${avgResolutionDays} j`} icon={IconGauge} additionalInfo="de la création à la clôture" description="Délai moyen entre la création et la clôture des tickets fermés sur la période." />
            <GPSKpiTile label="Interventions" value={interventions} previousValue={stats.previous.interventionsDone.length} icon={IconWrench} description="Tickets au statut « Terminé » avec une date d'intervention planifiée, comptés une fois par magasin et par jour." />
          </>
        )}
      </div>
    </section>
  );
};

export default GPSFluxBlock;
